import React from 'react';
import { motion } from 'framer-motion';
import { FileText, ChevronRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import Logo from '../components/Logo';

const Terms = () => {
  return (
    <div
      className="min-h-screen w-full"
      style={{
        background: 'linear-gradient(140deg, #08101e 0%, #0f172a 55%, #0b3b4b 100%)'
      }}
    >
      {/* Header */}
      <motion.header
        className="sticky top-0 z-40 backdrop-blur-xl border-b"
        style={{
          background: 'rgba(11, 18, 32, 0.7)',
          borderColor: 'rgba(var(--line), 0.6)'
        }}
        initial={{ y: -100, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.5 }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Logo />
            <Link
              to="/login"
              className="px-4 py-2 rounded-lg transition-all"
              style={{ background: 'var(--accent-cyan)', color: '#0b1220' }}
            >
              Retour
            </Link>
          </div>
        </div>
      </motion.header>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 py-12">
        {/* Hero */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="text-center mb-12"
        >
          <FileText size={64} className="mx-auto mb-4" style={{ color: 'var(--accent-cyan)' }} />
          <h1
            className="text-4xl font-bold mb-4"
            style={{
              background: 'linear-gradient(135deg, var(--accent-cyan) 0%, var(--accent-ocean) 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent'
            }}
          >
            Conditions Générales d'Utilisation
          </h1>
          <p className="text-lg" style={{ color: 'var(--text-secondary)' }}>
            Application Coach Life - OpenMeta
          </p>
        </motion.div>

        {/* Content Box */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
          className="backdrop-blur-xl rounded-2xl p-8 border space-y-8"
          style={{
            background: 'rgba(var(--bg-elev-1), 0.8)',
            borderColor: 'rgba(var(--line), 0.6)',
            color: 'var(--text-primary)'
          }}
        >
          {/* Warning */}
          <div
            className="p-4 rounded-lg border-l-4"
            style={{
              background: 'rgba(239, 68, 68, 0.1)',
              borderColor: '#ef4444'
            }}
          >
            <h3 className="font-bold mb-2 text-red-400">⚠️ Avertissement Important</h3>
            <p style={{ color: 'var(--text-secondary)' }}>
              Toute utilisation de l'Application est soumise au préalable à la prise de connaissance et à l'acceptation des présentes Conditions Générales d'Utilisation.
            </p>
          </div>

          {/* Section 1 */}
          <section>
            <h2 className="text-2xl font-bold mb-4" style={{ color: 'var(--accent-cyan)' }}>
              1. Mentions Légales
            </h2>
            
            <h3 className="text-xl font-semibold mb-2" style={{ color: 'var(--text-primary)' }}>
              1.1. Éditeur de l'Application
            </h3>
            <ul className="space-y-1 mb-4" style={{ color: 'var(--text-secondary)' }}>
              <li><ChevronRight size={16} className="inline mr-2" style={{ color: 'var(--accent-ocean)' }} />OpenMeta, pour la recherche et l'innovation</li>
              <li><ChevronRight size={16} className="inline mr-2" style={{ color: 'var(--accent-ocean)' }} />Centre de l'innovation technologique – 61330 Céaucé, FRANCE</li>
              <li><ChevronRight size={16} className="inline mr-2" style={{ color: 'var(--accent-ocean)' }} />Email : support@openmeta.fr</li>
              <li><ChevronRight size={16} className="inline mr-2" style={{ color: 'var(--accent-ocean)' }} />Directeur de la publication : Dr Yanis Belhatri</li>
            </ul>

            <h3 className="text-xl font-semibold mb-2" style={{ color: 'var(--text-primary)' }}>
              1.2. Hébergeur de l'Application
            </h3>
            <ul className="space-y-1" style={{ color: 'var(--text-secondary)' }}>
              <li><ChevronRight size={16} className="inline mr-2" style={{ color: 'var(--accent-ocean)' }} />OVH, société par actions simplifiées</li>
              <li><ChevronRight size={16} className="inline mr-2" style={{ color: 'var(--accent-ocean)' }} />SIREN : 424761419</li>
              <li><ChevronRight size={16} className="inline mr-2" style={{ color: 'var(--accent-ocean)' }} />Siège social : 2 rue Kellerman, 59100 ROUBAIX, FRANCE</li>
            </ul>
          </section>

          {/* Section 2 */}
          <section>
            <h2 className="text-2xl font-bold mb-4" style={{ color: 'var(--accent-cyan)' }}>
              2. Objet et Définitions
            </h2>
            <p className="mb-4" style={{ color: 'var(--text-secondary)' }}>
              Les Conditions Générales d'Utilisation sont destinées à encadrer l'utilisation de l'Application Coach Life.
            </p>
            <ul className="space-y-2" style={{ color: 'var(--text-secondary)' }}>
              <li><strong>Application :</strong> désigne l'application Coach Life éditée par OpenMeta, donnant accès à des Services et Contenus après création d'un compte sécurisé</li>
              <li><strong>CGU :</strong> désigne les présentes Conditions générales d'utilisation de l'Application</li>
              <li><strong>Compte Utilisateur :</strong> désigne le compte permettant à l'Utilisateur d'accéder aux Services et Contenus de l'Application</li>
              <li><strong>Contenus :</strong> désigne l'ensemble des textes, fonctionnalités, et plus largement tout élément existant au sein de l'Application</li>
            </ul>
          </section>

          {/* Section 6 */}
          <section>
            <h2 className="text-2xl font-bold mb-4" style={{ color: 'var(--accent-cyan)' }}>
              6. Présentation de l'Application
            </h2>
            <p className="mb-4" style={{ color: 'var(--text-secondary)' }}>
              L'Application Coach Life propose de mettre à disposition des Utilisateurs un coach IA conversationnel intelligent.
            </p>
            <p className="mb-2" style={{ color: 'var(--text-primary)' }}>L'Application donne accès aux fonctionnalités suivantes :</p>
            <ul className="space-y-2" style={{ color: 'var(--text-secondary)' }}>
              <li><ChevronRight size={16} className="inline mr-2" style={{ color: 'var(--accent-ocean)' }} />Chat conversationnel avec IA avancée (GPT-5.2)</li>
              <li><ChevronRight size={16} className="inline mr-2" style={{ color: 'var(--accent-ocean)' }} />Mode vocal en temps réel</li>
              <li><ChevronRight size={16} className="inline mr-2" style={{ color: 'var(--accent-ocean)' }} />Analyse d'images et de fichiers</li>
              <li><ChevronRight size={16} className="inline mr-2" style={{ color: 'var(--accent-ocean)' }} />Support multilingue (16 langues)</li>
              <li><ChevronRight size={16} className="inline mr-2" style={{ color: 'var(--accent-ocean)' }} />Historique des conversations</li>
            </ul>
          </section>

          {/* Section 10 */}
          <section>
            <h2 className="text-2xl font-bold mb-4" style={{ color: 'var(--accent-cyan)' }}>
              10. Propriété Intellectuelle
            </h2>
            <p style={{ color: 'var(--text-secondary)' }}>
              L'Éditeur est titulaire exclusif des droits de propriété intellectuelle et industrielle sur l'Application. L'utilisation de l'Application ne confère en aucun cas aux Utilisateurs un droit de propriété sur l'Application et ses Contenus.
            </p>
          </section>

          {/* Section 12 */}
          <section>
            <h2 className="text-2xl font-bold mb-4" style={{ color: 'var(--accent-cyan)' }}>
              12. Protection des Données à Caractère Personnel
            </h2>
            <p className="mb-4" style={{ color: 'var(--text-secondary)' }}>
              Des données à caractère personnel concernant l'Utilisateur sont collectées et traitées dans le cadre de l'accès et de l'utilisation de l'Application, conformément à la législation applicable relative à la protection des données à caractère personnel (RGPD).
            </p>
            <p style={{ color: 'var(--text-secondary)' }}>
              Pour exercer vos droits (accès, rectification, effacement, portabilité, opposition), contactez :
              <a href="mailto:support@openmeta.fr" className="ml-2" style={{ color: 'var(--accent-cyan)' }}>support@openmeta.fr</a>
            </p>
          </section>

          {/* Section 16 */}
          <section>
            <h2 className="text-2xl font-bold mb-4" style={{ color: 'var(--accent-cyan)' }}>
              16. Loi Française
            </h2>
            <p style={{ color: 'var(--text-secondary)' }}>
              L'Application a été conçue pour des Utilisateurs français. Les présentes CGU sont régies par la loi française. À défaut de règlement amiable, tout litige relatif à l'Application sera soumis aux tribunaux français.
            </p>
          </section>

          {/* Contact */}
          <section
            className="p-4 rounded-lg"
            style={{ background: 'rgba(6, 182, 212, 0.1)' }}
          >
            <h3 className="font-bold mb-2" style={{ color: 'var(--accent-cyan)' }}>Contact</h3>
            <p style={{ color: 'var(--text-secondary)' }}>
              Pour toute question concernant les présentes CGU, vous pouvez nous contacter à :
              <a href="mailto:support@openmeta.fr" className="ml-2" style={{ color: 'var(--accent-cyan)' }}>support@openmeta.fr</a>
            </p>
          </section>
        </motion.div>

        {/* Privacy Link */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5 }}
          className="mt-6 text-center"
        >
          <Link
            to="/privacy"
            className="text-sm hover:underline"
            style={{ color: 'var(--accent-ocean)' }}
          >
            Politique de confidentialité →
          </Link>
        </motion.div>
      </div>
    </div>
  );
};

export default Terms;
